// COMMENTS tab — the real pane (F-COMMENTS, slice A). Composes the review
// thread list (pure BE-consumption via `useComments`) + the server-side
// open/resolved filter chips + an honest ComingSoon for anchored-create
// (Office.js-deferred, F-COMMENTS-ANCHOR). Mirrors `VersionsPane`'s
// load-on-active-brief posture.
//
// Invariants:
//   W1 — no business logic. Resolution state + the open/resolved filtering are
//        BE-owned (the filter is a SERVER query param); the pane only projects
//        and dispatches port calls.
//   W2 — no Office.js. Anchored-create is ComingSoon precisely because it needs
//        a `Word.run` selection reader (deferred).
//   W5 — no new store. Reuses `useCaseStore` (already wired into logout reset).
//   W6 — no $/tokens. Threads show author/date/body only.
//   §9 — logical RTL utilities; BE author/body/anchor text get `bidiProps`
//        (inside CommentCard).

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useComments } from '@/ui/hooks/use-comments'
import { useCaseStore } from '@/ui/stores/use-case-store'
import { CommentFilterChips } from './CommentFilterChips'
import { CommentThreadList } from './CommentThreadList'
import { AnchorComingSoon } from './AnchorComingSoon'

export function CommentsPane(): JSX.Element {
  const { t } = useTranslation()
  const {
    threads,
    total,
    loading,
    loadingMore,
    hasMore,
    error,
    filter,
    setFilter,
    addComment,
    resolveThread,
    reload,
    loadMore,
  } = useComments()
  const activeBriefId = useCaseStore((s) => s.activeContext?.briefId ?? null)

  // Load the active brief's threads when a brief becomes active.
  useEffect(() => {
    if (activeBriefId) void reload()
  }, [activeBriefId, reload])

  // ── Gate: no active brief → honest empty hint (mirror VersionsPane) ───────
  if (activeBriefId == null) {
    return (
      <div data-testid="comments-pane" className="flex flex-1 flex-col">
        <div
          data-testid="comments-empty-no-brief"
          className="border-b border-ink/10 px-3.5 py-4"
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-ink/40">
            {t('comments.header')}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-ink/60">
            {t('comments.noBriefHint')}
          </p>
        </div>
        <AnchorComingSoon />
      </div>
    )
  }

  return (
    <div data-testid="comments-pane" className="flex flex-1 flex-col">
      <div className="border-b border-ink/10 px-3.5 py-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-ink/40">
          {t('comments.header')}
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-ink/60">
          {t('comments.help')}
        </p>
      </div>

      <CommentFilterChips
        filter={filter}
        onChange={(next) => void setFilter(next)}
        disabled={loading}
      />

      <div className="flex flex-1 flex-col overflow-y-auto">
        {loading && (
          <div
            data-testid="comments-loading"
            className="px-3.5 py-3 text-xs text-ink/60"
          >
            {t('comments.loading')}
          </div>
        )}

        {error && (
          <div
            role="alert"
            data-testid="comments-error"
            className="m-2 rounded-md border-s-2 border-warn bg-warn/5 px-2.5 py-1.5 text-[11px] text-ink/80"
          >
            {error.message}
          </div>
        )}

        {!loading && !error && (
          <CommentThreadList
            threads={threads}
            onReply={(threadId, body) => void addComment(threadId, body)}
            onToggleResolve={(threadId, isResolved) =>
              void resolveThread(threadId, isResolved)
            }
          />
        )}

        {!loading && !error && hasMore && (
          <div className="px-3.5 py-3">
            <button
              type="button"
              data-testid="comments-load-more"
              disabled={loadingMore}
              onClick={() => void loadMore()}
              className={[
                'w-full rounded-md border border-ink/15 px-3 py-1.5 text-[11px] font-medium text-ink/70 transition-colors',
                loadingMore
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:bg-ink/5',
              ].join(' ')}
            >
              {loadingMore
                ? t('comments.loadingMore')
                : t('comments.loadMore', {
                    shown: threads.length,
                    total,
                  })}
            </button>
          </div>
        )}
      </div>

      <AnchorComingSoon />
    </div>
  )
}
